import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, IMG } from "@/data/site";

const Logo = () => (
  <a href="#home" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <img src={IMG.logo} alt="SuhaniEdge logo" className="h-10 w-10 rounded-xl object-cover ring-1 ring-white/10 shadow-[0_0_24px_rgba(139,92,246,0.4)]" />
    <span className="font-heading text-xl font-semibold tracking-tight text-white">
      Suhani<span className="text-brand-light">Edge</span>
    </span>
  </a>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 lg:px-10 py-3"
        data-testid="navbar"
      >
        <div className={`mx-auto max-w-7xl flex items-center justify-between rounded-2xl px-4 sm:px-6 py-3 transition-all duration-300 ${scrolled ? "glass shadow-[0_8px_40px_rgba(0,0,0,0.4)]" : "bg-transparent"}`}>
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brand-light hover:after:w-full after:transition-all after:duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button data-testid="nav-login-btn" className="text-sm font-medium text-slate-400 hover:text-white transition-colors px-3 py-2" title="Coming soon">
              Login
            </button>
            <a href="#courses" data-testid="nav-explore-btn" className="group inline-flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-light text-white text-sm font-semibold px-5 py-2.5 transition-colors">
              Explore Courses
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(true)} className="lg:hidden text-white p-2" aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/98 backdrop-blur-lg flex flex-col lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white p-2" aria-label="Close menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-2 px-6 mt-6">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-heading text-3xl font-semibold text-white py-2 border-b border-white/5"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="mt-auto px-6 pb-10 flex flex-col gap-3">
              <a href="#courses" onClick={() => setOpen(false)} className="w-full text-center rounded-full bg-brand text-white font-semibold py-4">
                Explore Courses
              </a>
              <button className="w-full text-center rounded-full border border-white/15 text-white font-medium py-4">Login</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
